import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Login as BLLogin } from '../../utilities/BL/Login';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe usarse dentro de un AuthProvider');
    }
    return context;
};

const getApiBaseUrl = () => {
    const envUrl = process.env.REACT_APP_API_URL;
    if (envUrl) {
        return envUrl.endsWith('/') ? envUrl : `${envUrl}/`;
    }
    return '/api/';
};

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(() => {
        try {
            return localStorage.getItem('token') || null;
        } catch {
            return null;
        }
    });

    const [refreshToken, setRefreshToken] = useState(() => {
        try {
            return localStorage.getItem('refreshToken') || null;
        } catch {
            return null;
        }
    });

    const [user, setUser] = useState(() => {
        try {
            const rawUser = localStorage.getItem('user');
            return rawUser ? JSON.parse(rawUser) : null;
        } catch {
            return null;
        }
    });

    const [role, setRole] = useState(() => {
        try {
            const storedRole = localStorage.getItem('role');
            if (storedRole) return storedRole;
            const rawUser = localStorage.getItem('user');
            if (rawUser) {
                const parsed = JSON.parse(rawUser);
                return parsed?.role || null;
            }
            return null;
        } catch {
            return null;
        }
    });

    const [isLoading, setIsLoading] = useState(false);

    const isAuthenticated = Boolean(token);

    const logout = useCallback(async () => {
        const currentRefreshToken = refreshToken || localStorage.getItem('refreshToken');
        try {
            if (currentRefreshToken) {
                const baseUrl = getApiBaseUrl();
                await fetch(`${baseUrl}Authentication/logout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ refreshToken: currentRefreshToken }),
                });
            }
        } catch (error) {
            console.error('Error al invocar endpoint de logout:', error);
        } finally {
            try {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('role');
                localStorage.removeItem('user');
            } catch (err) {
                console.warn('Error al limpiar localStorage:', err);
            }
            setToken(null);
            setRefreshToken(null);
            setUser(null);
            setRole(null);
        }
    }, [refreshToken]);

    const refreshAccessToken = useCallback(async () => {
        const currentRefreshToken = refreshToken || localStorage.getItem('refreshToken');
        if (!currentRefreshToken) {
            await logout();
            return null;
        }

        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}Authentication/refresh-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken: currentRefreshToken }),
            });

            if (!response.ok) {
                await logout();
                return null;
            }

            const data = await response.json();
            if (data && data.token) {
                const newToken = data.token;
                const newRefreshToken = data.refreshToken || currentRefreshToken;
                const newUser = data.user || null;
                const newRole = newUser?.role || data.role || (newUser ? newUser.role : null);

                try {
                    localStorage.setItem('token', newToken);
                    if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
                    if (newUser) localStorage.setItem('user', JSON.stringify(newUser));
                    if (newRole) localStorage.setItem('role', newRole);
                } catch (e) {
                    console.warn('Error guardando token renovado en localStorage:', e);
                }

                setToken(newToken);
                if (newRefreshToken) setRefreshToken(newRefreshToken);
                if (newUser) setUser(newUser);
                if (newRole) setRole(newRole);

                return newToken;
            } else {
                await logout();
                return null;
            }
        } catch (error) {
            console.error('Error durante la renovación de token:', error);
            await logout();
            return null;
        }
    }, [refreshToken, logout]);

    const login = useCallback(async (username, password) => {
        setIsLoading(true);
        try {
            const response = await BLLogin(username, password);
            if (response.success && response.data) {
                const newToken = response.data.token;
                const newRefreshToken = response.data.refreshToken;
                const newUser = response.data.user;
                const newRole = newUser?.role || response.data.role || '';

                setToken(newToken);
                setRefreshToken(newRefreshToken);
                setUser(newUser);
                setRole(newRole);

                try {
                    if (newToken) localStorage.setItem('token', newToken);
                    if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
                    if (newUser) localStorage.setItem('user', JSON.stringify(newUser));
                    if (newRole) localStorage.setItem('role', newRole);
                } catch (e) {
                    console.warn('Error guardando sesión:', e);
                }
            }
            return response;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const authenticatedFetch = useCallback(async (url, options = {}) => {
        const currentToken = token || localStorage.getItem('token');
        const headers = new Headers(options.headers || {});

        if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
            headers.set('Content-Type', 'application/json');
        }

        if (currentToken) {
            headers.set('Authorization', `Bearer ${currentToken}`);
        }

        const fetchOptions = {
            ...options,
            headers,
        };

        let response = await fetch(url, fetchOptions);

        if (response.status === 401) {
            const newToken = await refreshAccessToken();
            if (newToken) {
                const retryHeaders = new Headers(options.headers || {});
                if (!retryHeaders.has('Content-Type') && !(options.body instanceof FormData)) {
                    retryHeaders.set('Content-Type', 'application/json');
                }
                retryHeaders.set('Authorization', `Bearer ${newToken}`);
                response = await fetch(url, {
                    ...options,
                    headers: retryHeaders,
                });
            } else {
                await logout();
            }
        }

        return response;
    }, [token, refreshAccessToken, logout]);

    const hasRole = useCallback((allowedRoles) => {
        if (!allowedRoles || (Array.isArray(allowedRoles) && allowedRoles.length === 0)) {
            return true;
        }
        if (!role) {
            return false;
        }
        const rolesArray = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
        const normalizedCurrentRole = role.toString().trim().toLowerCase();
        return rolesArray.some((r) => r.toString().trim().toLowerCase() === normalizedCurrentRole);
    }, [role]);

    const value = useMemo(() => ({
        user,
        token,
        refreshToken,
        role,
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshAccessToken,
        authenticatedFetch,
        hasRole,
    }), [user, token, refreshToken, role, isAuthenticated, isLoading, login, logout, refreshAccessToken, authenticatedFetch, hasRole]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
